<template>
  <div v-if="sol" class="sol" :style="{ '--accent': accent, '--accent2': accent2 }">
    <!-- ============ 首屏 ============ -->
    <section class="sol-hero">
      <div class="sol-hero-bg">
        <img :src="img(sol.hero || line.hero)" :alt="`${sol.name} - 皓辰科技`"/>
        <div class="sol-hero-mask"></div>
        <TechCanvas class="sol-canvas" :color="canvasColor" :link-color="canvasColor"/>
        <div class="grid-bg"></div>
      </div>

      <div class="page sol-hero-inner">
        <nav class="crumb">
          <router-link to="/home">首页</router-link>
          <span>/</span>
          <router-link :to="line.path">{{ line.name }}</router-link>
          <span>/</span>
          <em>{{ sol.name }}</em>
        </nav>

        <span class="eyebrow reveal" v-reveal>{{ sol.eyebrow }}</span>
        <h1 class="h1 sol-title reveal" v-reveal="{ delay: 80 }">
          {{ sol.name }}
          <span class="sol-tagline">{{ sol.tagline }}</span>
        </h1>
        <p class="sol-summary reveal" v-reveal="{ delay: 160 }">{{ sol.summary }}</p>

        <div class="sol-actions reveal" v-reveal="{ delay: 240 }">
          <router-link to="/contact" class="btn btn-primary">
            获取{{ sol.name }}方案
            <IconSvg name="arrow-right"/>
          </router-link>
          <a class="btn btn-ghost" :href="`tel:${company.phone}`">
            <IconSvg name="phone"/> {{ company.phone }}
          </a>
        </div>

        <ul class="sol-highlights reveal" v-reveal="{ delay: 320 }">
          <li v-for="(c, i) in sol.capabilities.slice(0, 3)" :key="i">
            <IconSvg :name="c.icon"/>
            <span>{{ c.title }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- ============ 概述 + 典型困境 ============ -->
    <section class="sec">
      <div class="page">
        <div class="overview">
          <div class="overview-text reveal" v-reveal>
            <span class="eyebrow">Overview</span>
            <h2 class="h2">这一层要解决的问题</h2>
            <p class="lead">{{ sol.overview }}</p>
          </div>
          <div class="pains">
            <div
              v-for="(p, i) in sol.pains"
              :key="i"
              class="pain reveal"
              v-reveal="{ delay: i * 100 }"
            >
              <span class="pain-num">0{{ i + 1 }}</span>
              <h3>{{ p.title }}</h3>
              <p>{{ p.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ 能力清单 ============ -->
    <section class="sec sec-tint">
      <div class="page">
        <SectionHeading
          eyebrow="Capabilities"
          :title="`${sol.name}包含哪些能力`"
          desc="以下每一项都可独立交付、单独验收，先上哪几项由现场条件与目标决定。"
        />
        <div class="cap-grid">
          <div
            v-for="(c, i) in sol.capabilities"
            :key="c.title"
            class="cap-card card card-pad reveal"
            v-reveal="{ delay: (i % 3) * 90 }"
          >
            <div class="cap-icon"><IconSvg :name="c.icon"/></div>
            <h3 class="cap-title">{{ c.title }}</h3>
            <p class="cap-desc">{{ c.desc }}</p>
            <div class="cap-tags">
              <span v-for="(t, ti) in c.tags" :key="ti" class="tag">{{ t }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ 怎么运转 ============ -->
    <section class="sec">
      <div class="page">
        <SectionHeading
          eyebrow="How it works"
          title="从现场到系统，五步跑通"
          desc="每一步都有明确产出物，避免「需求理解偏差」变成项目风险。"
        />
        <ol class="steps">
          <li
            v-for="(h, i) in sol.how"
            :key="h.title"
            class="step reveal"
            v-reveal="{ delay: i * 90 }"
          >
            <span class="step-index">{{ i + 1 }}</span>
            <div class="step-body">
              <h3>{{ h.title }}</h3>
              <p>{{ h.desc }}</p>
            </div>
            <span v-if="i < sol.how.length - 1" class="step-line"></span>
          </li>
        </ol>
      </div>
    </section>

    <!-- ============ 关键实现与边界 ============ -->
    <section class="sec sec-tint">
      <div class="page">
        <SectionHeading
          eyebrow="Details"
          title="关键实现与口径边界"
          desc="把实现方式和边界写清楚，选型时更容易判断是否符合你的现场要求。"
        />
        <div class="spec-wrap">
          <dl class="spec-list reveal" v-reveal>
            <div v-for="(s, i) in sol.specs" :key="i" class="spec-row">
              <dt>{{ s.k }}</dt>
              <dd>{{ s.v }}</dd>
            </div>
          </dl>
          <aside class="spec-side reveal" v-reveal="{ delay: 120 }">
            <h3 class="h3">需要现场确认的部分</h3>
            <p class="spec-note">
              下列内容与具体点位、设备型号和现场条件强相关，需勘查后确认，
              <strong>我们不预先给不负责的承诺</strong>：
            </p>
            <ul class="spec-side-list">
              <li><IconSvg name="check"/><span>可接入的设备型号与通信接口</span></li>
              <li><IconSvg name="check"/><span>点位数量、覆盖范围与安装方式</span></li>
              <li><IconSvg name="check"/><span>是否需要对现有设备或控制器改造</span></li>
              <li><IconSvg name="check"/><span>实施周期与分阶段排期</span></li>
            </ul>
            <router-link to="/contact" class="btn btn-ghost spec-btn">
              带着现场情况来沟通
              <IconSvg name="arrow-right"/>
            </router-link>
          </aside>
        </div>
      </div>
    </section>

    <!-- ============ 常见问题 ============ -->
    <section class="sec">
      <div class="page">
        <SectionHeading
          eyebrow="FAQ"
          title="常见问题"
          desc="如果这里没有覆盖你的疑问，直接打电话或到访，我们把细节聊清楚。"
        />
        <FaqList :items="sol.faq"/>
      </div>
    </section>

    <!-- ============ 相关能力（内链） ============ -->
    <section class="sec-sm related">
      <div class="page">
        <div class="related-head"><span class="eyebrow">Related</span></div>
        <div class="related-grid">
          <router-link
            v-for="r in related"
            :key="r.slug"
            :to="`/solutions/${r.slug}`"
            class="related-card reveal"
            v-reveal
          >
            <h3>{{ r.name }}</h3>
            <p>{{ r.tagline }}</p>
            <span class="related-link">查看能力详情 <IconSvg name="arrow-right"/></span>
          </router-link>
        </div>
        <div class="related-back">
          <router-link :to="line.path" class="btn-link">
            返回「{{ line.name }}」业务线总览 <IconSvg name="arrow-right"/>
          </router-link>
        </div>
      </div>
    </section>

    <!-- ============ 延伸阅读（内链到深度文章） ============ -->
    <!-- 反向通道：看功能域页的人是「已在评估具体能力」的读者，
         他们最需要的是「这件事怎么做、坑在哪」的深度文章。 -->
    <section class="sec-sm reading" v-if="relatedPosts.length">
      <div class="page">
        <div class="rd-head">
          <span class="eyebrow">Further reading</span>
          <h2 class="rd-title">想先看这件事怎么做、坑在哪</h2>
        </div>
        <div class="rd-list">
          <router-link
            v-for="p in relatedPosts"
            :key="p.slug"
            :to="`/insights/${p.slug}`"
            class="rd-item"
          >
            <span class="rd-cat">{{ p.category }}</span>
            <span class="rd-name">{{ p.title }}</span>
            <span class="rd-mins">{{ p.minutes }} 分钟</span>
            <IconSvg name="arrow-right" class="rd-arrow"/>
          </router-link>
        </div>
      </div>
    </section>

    <CtaBand
      :eyebrow="line.en"
      :title="`现在开始推进${sol.name}`"
      desc="把现场情况讲清楚，皓辰给出可落地的范围、周期与投入建议。"
      :bg="img('gen/cta-bg.jpg')"
    />
  </div>
</template>

<script>
import TechCanvas from '@/components/common/TechCanvas'
import IconSvg from '@/components/common/IconSvg'
import SectionHeading from '@/components/common/SectionHeading'
import CtaBand from '@/components/common/CtaBand'
import FaqList from '@/components/common/FaqList'
import img from '@/utils/assets'
import { company, businessLines } from '@/data/site'
import { getSolution } from '@/data/solutions'
import { getPost } from '@/data/posts'
import { POSTS_FOR_SOLUTION } from '@/data/solution-posts'

const SITE = 'https://www.hoyotech.top'
const LD_ID = 'ld-solution'
const BASE_TITLE = '皓辰科技 HOYOTECH'

export default {
  name: 'SolutionPage',
  components: { TechCanvas, IconSvg, SectionHeading, CtaBand, FaqList },
  props: {
    slug: { type: String, required: true }
  },
  data() {
    return { company, img }
  },
  computed: {
    sol() {
      return getSolution(this.slug)
    },
    line() {
      const s = this.sol
      return businessLines.find((l) => l.key === (s && s.line)) || businessLines[0]
    },
    accent() {
      return this.line.accent
    },
    accent2() {
      return this.line.accent2
    },
    canvasColor() {
      const hex = this.accent || '#22e0ff'
      const n = parseInt(hex.slice(1), 16)
      return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
    },
    related() {
      const s = this.sol
      if (!s) return []
      return (s.related || []).map((k) => getSolution(k)).filter(Boolean)
    },
    /**
     * 延伸阅读：本功能域对应的深度文章（功能域页 → 文章的反向内链）。
     * 与文章页的 relatedSolutions 合起来构成 文章 ⇄ 功能域 双向通道。
     * 取不到的 slug 一律过滤，绝不渲染死链。
     */
    relatedPosts() {
      const keys = POSTS_FOR_SOLUTION[this.slug] || []
      return keys.map((k) => getPost(k)).filter(Boolean)
    },
    /** Service + FAQPage + BreadcrumbList：让搜索引擎识别这一页讲什么、属于哪条业务线 */
    jsonLd() {
      const s = this.sol
      if (!s) return null
      const url = `${SITE}/solutions/${s.slug}`
      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Service',
            '@id': `${url}#service`,
            name: s.name,
            serviceType: s.name,
            description: s.summary,
            url,
            provider: { '@type': 'Organization', name: company.name, url: `${SITE}/home` },
            areaServed: { '@type': 'Country', name: '中国' },
            isPartOf: { '@type': 'Service', name: `${this.line.name}解决方案`, url: `${SITE}${this.line.path}` },
            hasOfferCatalog: {
              '@type': 'OfferCatalog',
              name: `${s.name}包含的能力`,
              itemListElement: (s.capabilities || []).map((c, i) => ({
                '@type': 'Offer',
                position: i + 1,
                name: c.title,
                description: c.desc
              }))
            }
          },
          {
            '@type': 'FAQPage',
            '@id': `${url}#faq`,
            mainEntity: (s.faq || []).map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a }
            }))
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE}/home` },
              { '@type': 'ListItem', position: 2, name: this.line.name, item: `${SITE}${this.line.path}` },
              { '@type': 'ListItem', position: 3, name: s.name, item: url }
            ]
          }
        ]
      }
    }
  },
  watch: {
    // 相邻解决方案页之间切换时组件会被复用，必须重新注入结构化数据
    slug() { this.syncLd() },
    immediate: false
  },
  mounted() { this.syncLd() },
  beforeDestroy() {
    const el = document.getElementById(LD_ID)
    if (el) el.remove()
  },
  methods: {
    /** 本页 title / description 由 slug 决定（router.afterEach 对 name==='solution' 已跳过） */
    syncMeta() {
      const s = this.sol
      if (!s) return
      document.title = `${s.name} · ${this.line.name} · ${BASE_TITLE}`
      let el = document.querySelector('meta[name="description"]')
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute('name', 'description')
        document.head.appendChild(el)
      }
      el.setAttribute('content', s.summary)
    },
    syncLd() {
      this.syncMeta()
      const data = this.jsonLd
      if (!data) return
      let el = document.getElementById(LD_ID)
      if (!el) {
        el = document.createElement('script')
        el.type = 'application/ld+json'
        el.id = LD_ID
        document.head.appendChild(el)
      }
      el.textContent = JSON.stringify(data)
    }
  }
}
</script>

<style lang="scss" scoped>
.sol { position: relative; }

/* ---------------- 首屏 ---------------- */
.sol-hero {
  position: relative;
  min-height: 76vh;
  display: flex;
  align-items: center;
  padding: calc(var(--nav-h) + 46px) 0 80px;
  overflow: hidden;
}

.sol-hero-bg {
  position: absolute;
  inset: 0;

  img { width: 100%; height: 100%; object-fit: cover; opacity: 0.46; }

  .sol-hero-mask {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 66% 60% at 20% 40%, color-mix(in srgb, var(--accent) 22%, transparent), transparent 68%),
      linear-gradient(180deg, rgba(3, 7, 17, 0.9), rgba(3, 7, 17, 0.76) 45%, rgba(3, 7, 17, 0.98));
  }

  .sol-canvas { opacity: 0.55; }
}

.sol-hero-inner { position: relative; z-index: 2; max-width: 900px; }

.crumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 26px;
  font-size: 13px;
  color: var(--txt-3);

  a { display: inline-block; padding: 7px 2px; }
  a:hover { color: var(--accent-txt); }
  em { font-style: normal; color: var(--accent-txt); }
}

.sol-title {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  .sol-tagline {
    font-size: clamp(19px, 2.3vw, 30px);
    font-weight: 700;
    background: linear-gradient(100deg, var(--accent), var(--accent2));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.sol-summary {
  margin-top: 24px;
  max-width: 760px;
  font-size: 16.5px;
  line-height: 1.95;
  color: var(--txt-2);
}

.sol-actions { display: flex; gap: 16px; margin-top: 34px; flex-wrap: wrap; }

.sol-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 42px;

  li {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 11px 18px;
    border-radius: 999px;
    border: 1px solid var(--line);
    background: rgba(255, 255, 255, 0.035);
    font-size: 14px;
    color: var(--txt-2);
    backdrop-filter: blur(8px);

    svg { width: 17px; height: 17px; color: var(--accent-txt); }
  }
}

/* ---------------- 概述 + 困境 ---------------- */
.overview {
  display: grid;
  grid-template-columns: 1fr 1.05fr;
  gap: 56px;
  align-items: start;
}

.overview-text {
  .h2 { margin-top: 16px; }
  .lead { margin-top: 18px; }
}

.pains { display: flex; flex-direction: column; gap: 14px; }

.pain {
  position: relative;
  padding: 24px 26px 24px 76px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: linear-gradient(120deg, rgba(11, 23, 48, 0.66), rgba(5, 12, 27, 0.5));
  transition: transform 0.45s, border-color 0.45s;

  &:hover { transform: translateX(6px); border-color: color-mix(in srgb, var(--accent) 40%, transparent); }

  .pain-num {
    position: absolute;
    left: 26px;
    top: 24px;
    font-size: 15px;
    font-weight: 800;
    color: var(--accent-txt);
    opacity: 0.85;
  }

  h3 { font-size: 17px; color: #fff; }
  p { margin-top: 8px; font-size: 14px; line-height: 1.85; color: var(--txt-3); }
}

/* ---------------- 能力 ---------------- */
.cap-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px; }

.cap-card {
  display: flex;
  flex-direction: column;

  &:hover .cap-icon { color: var(--accent-txt); border-color: var(--accent); box-shadow: 0 0 26px -8px var(--accent); }
}

.cap-icon {
  width: 50px;
  height: 50px;
  padding: 13px;
  border-radius: 14px;
  border: 1px solid var(--line-strong);
  color: var(--txt-2);
  background: rgba(255, 255, 255, 0.03);
  transition: all 0.4s;
}

.cap-title { margin-top: 20px; font-size: 18px; color: #fff; }
.cap-desc { margin-top: 11px; font-size: 14px; line-height: 1.85; color: var(--txt-3); flex: 1; }

.cap-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(120, 176, 255, 0.1);

  .tag { height: 24px; font-size: 12px; padding: 0 10px; }
}

/* ---------------- 步骤 ---------------- */
.steps { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 20px; }

.step {
  position: relative;
  padding: 26px 20px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(11, 23, 48, 0.7), rgba(5, 12, 27, 0.55));
  transition: transform 0.45s, border-color 0.45s;

  &:hover { transform: translateY(-6px); border-color: color-mix(in srgb, var(--accent) 45%, transparent); }
  &:hover .step-index { color: #01060f; background: var(--accent); }

  .step-index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    font-size: 14px;
    font-weight: 800;
    color: var(--accent-txt);
    border: 1px solid var(--accent);
    transition: all 0.4s;
  }

  .step-body {
    margin-top: 16px;
    h3 { font-size: 16px; color: #fff; }
    p { margin-top: 8px; font-size: 13.5px; line-height: 1.8; color: var(--txt-3); }
  }

  .step-line {
    position: absolute;
    top: 40px;
    right: -14px;
    width: 26px;
    height: 1px;
    background: linear-gradient(90deg, color-mix(in srgb, var(--accent) 60%, transparent), transparent);
  }
}

/* ---------------- 口径边界 ---------------- */
.spec-wrap { display: grid; grid-template-columns: 1.3fr 0.9fr; gap: 40px; align-items: start; }

.spec-list {
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(11, 23, 48, 0.62), rgba(5, 12, 27, 0.5));
  overflow: hidden;
}

.spec-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 18px;
  padding: 18px 24px;
  border-bottom: 1px solid rgba(120, 176, 255, 0.09);

  &:last-child { border-bottom: none; }

  dt {
    font-size: 13.5px;
    font-weight: 700;
    color: var(--accent-txt);
    letter-spacing: 0.02em;
  }

  dd { font-size: 14px; line-height: 1.85; color: var(--txt-2); }
}

.spec-side {
  padding: 28px 26px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(13, 27, 54, 0.72), rgba(5, 12, 27, 0.6));

  .h3 { color: #fff; }
}

.spec-note { margin-top: 14px; font-size: 14px; line-height: 1.85; color: var(--txt-3); }
.spec-side-list {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li { display: flex; gap: 11px; font-size: 14px; line-height: 1.75; color: var(--txt-3); }
  svg { width: 17px; height: 17px; flex-shrink: 0; margin-top: 4px; color: var(--accent-txt); }
}
.spec-btn { margin-top: 24px; }

/* ---------------- 相关能力 ---------------- */
.related-head { margin-bottom: 20px; }
.related-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }

.related-card {
  padding: 26px 28px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(140deg, rgba(12, 26, 52, 0.7), rgba(5, 12, 27, 0.55));
  transition: transform 0.45s, border-color 0.45s, box-shadow 0.45s;

  h3 { font-size: 19px; color: #fff; }
  p { margin-top: 10px; font-size: 14px; color: var(--txt-3); }
  .related-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 18px;
    font-size: 14px;
    color: var(--accent-txt);
    svg { width: 17px; height: 17px; transition: transform 0.4s; }
  }

  &:hover { transform: translateY(-6px); border-color: color-mix(in srgb, var(--accent) 45%, transparent); box-shadow: 0 26px 60px -34px var(--accent); }
  &:hover .related-link svg { transform: translateX(5px); }
}

.related-back { margin-top: 28px; }

/* ---------------- 延伸阅读（功能域页 → 文章，反向内链） ---------------- */
/* 刻意做成轻量列表而不是大卡片：上面的「相关能力」已经是卡片区，
   这里再用卡片会抢注意力，也不利于读者一眼扫到文章标题。 */
.reading { padding-top: 0; }

.rd-head {
  margin-bottom: 22px;
  .rd-title { margin-top: 12px; font-size: 24px; color: #fff; }
}

.rd-list { display: flex; flex-direction: column; gap: 12px; }

.rd-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 22px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: linear-gradient(140deg, rgba(12, 26, 52, 0.55), rgba(5, 12, 27, 0.45));
  transition: border-color 0.35s, transform 0.35s;

  .rd-cat {
    flex: 0 0 auto;
    padding: 3px 11px;
    border-radius: 999px;
    font-size: 12.5px;
    color: var(--accent-txt);
    border: 1px solid color-mix(in srgb, var(--accent) 35%, transparent);
  }
  .rd-name { flex: 1 1 auto; min-width: 0; font-size: 15.5px; color: var(--txt); }
  .rd-mins { flex: 0 0 auto; font-size: 13px; color: var(--txt-3); }
  .rd-arrow { flex: 0 0 auto; width: 16px; height: 16px; color: var(--accent-txt); transition: transform 0.35s; }

  &:hover {
    border-color: color-mix(in srgb, var(--accent) 40%, transparent);
    transform: translateX(4px);
  }
  &:hover .rd-arrow { transform: translateX(4px); }
}

/* ---------------- 响应式 ---------------- */
@media (max-width: 1080px) {
  .overview { grid-template-columns: minmax(0, 1fr); gap: 32px; }
  .cap-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .steps { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .step-line { display: none; }
  .spec-wrap { grid-template-columns: minmax(0, 1fr); gap: 26px; }
}

@media (max-width: 760px) {
  /* 延伸阅读条目：标题换行到单独一行，避免长标题把分类与时长挤没 */
  .rd-item {
    flex-wrap: wrap;
    gap: 8px 12px;
    padding: 16px 18px;
    .rd-name { flex: 1 1 100%; order: 3; font-size: 15px; }
  }
  .rd-head .rd-title { font-size: 21px; }
  .sol-hero { min-height: auto; padding: calc(var(--nav-h) + 30px) 0 56px; }
  .sol-summary { font-size: 15px; }
  .sol-highlights { gap: 10px; margin-top: 28px; }
  .sol-highlights li { padding: 9px 14px; font-size: 13px; }
  .cap-grid, .steps, .related-grid { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  .pain { padding: 20px 20px 20px 62px; }
  .pain .pain-num { left: 20px; top: 20px; }
  .spec-row { grid-template-columns: minmax(0, 1fr); gap: 6px; padding: 16px 18px; }
}
</style>
